import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateOnly } from '../../../common/utils/date';
import { useProjectedCrossesList } from '../contexts/projected-crosses';
import { useCrossQueues } from '../contexts/queues';

import './styles/view.css';

// Pestaña "Cola" de Cruces proyecto (decisión 0086 · Q-255). La cola cuelga de la
// donadora, así que se elige un cruce y se consulta la cola de su donadora, filtrada a
// ese cruce: encargos TOMADOS (cubiertos por una preñez confirmada) y LIBRES (sin cubrir
// + vacantes reabiertas por aborto). Orden FIFO. El backend materializa; el front muestra.
const REOPENED = { abortion: 'Vacante por aborto', embryonic_loss: 'Vacante por pérdida embrionaria' };

function BuyerLink({ id, name }) {
    if (!id) return <span className="pcx-q__buyer">{name || '—'}</span>;
    return <Link to={`/admin/clients/${id}`} className="pcx-q__buyer">{name || `#${id}`}</Link>;
}

export default function ProjectedCrossQueue() {
    const { data, isLoading: crossesLoading } = useProjectedCrossesList({ limit: 500, sort: 'code', status: 2 });
    const crosses = useMemo(() => data?.items ?? [], [data]);
    const [crossId, setCrossId] = useState('');

    const cross = crosses.find((c) => String(c.id) === String(crossId));
    const donorId = cross?.donor?.id;
    const { data: queues, isFetching, error } = useCrossQueues(donorId);

    const q = useMemo(() => (queues || []).find((x) => String(x.cross_id) === String(cross?.id)), [queues, cross]);
    const taken = q?.taken || [];
    const free = q?.free || [];

    return (
        <div className="pcx-queue">
            <div className="pcx-queue__pick">
                <label className="pcx-queue__pick-field">
                    <span className="pcx-queue__pick-label">Cruce</span>
                    <select value={crossId} onChange={(e) => setCrossId(e.target.value)} disabled={crossesLoading}>
                        <option value="">Elegí un cruce…</option>
                        {crosses.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.code || `#${c.id}`}{c.donor?.name ? ` · ${c.donor.name}` : ''}{c.sire?.name ? ` × ${c.sire.name}` : ''}
                            </option>
                        ))}
                    </select>
                </label>
                {cross && <span className="pcx-queue__hint">Cola de la donadora {cross.donor?.name || `#${donorId}`}.</span>}
            </div>

            {!cross ? (
                <p className="pcx-queue__empty">Elegí un cruce para ver su cola de encargos.</p>
            ) : error ? (
                <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver la cola.' : (error.message || 'No se pudo cargar la cola.')}</div>
            ) : isFetching && !queues ? (
                <p className="pcx-queue__empty">Cargando cola…</p>
            ) : (
                <div className="pcx-queue__cols">
                    <section className="pcx-col">
                        <h3 className="pcx-col__title">Tomados <span className="pcx-col__count">{taken.length}</span></h3>
                        {!taken.length ? (
                            <p className="pcx-col__empty">Sin encargos cubiertos por una preñez.</p>
                        ) : (
                            <ul className="pcx-slots">
                                {taken.map((t) => (
                                    <li key={t.sale_id} className="pcx-slot pcx-slot--taken">
                                        <BuyerLink id={t.buyer_id} name={t.buyer} />
                                        <span className="pcx-slot__meta">
                                            <span className="pcx-slot__preg">Preñez #{t.pregnancy_id}</span>
                                            {t.estimated_calving && <span className="pcx-slot__calving">Parto est. {formatDateOnly(t.estimated_calving)}</span>}
                                        </span>
                                        {t.matched_at && <span className="pcx-slot__since">Tomado {formatDateOnly(t.matched_at)}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className="pcx-col">
                        <h3 className="pcx-col__title">Libres <span className="pcx-col__count">{free.length}</span></h3>
                        {!free.length ? (
                            <p className="pcx-col__empty">Sin encargos libres.</p>
                        ) : (
                            <ul className="pcx-slots">
                                {free.map((f) => (
                                    <li key={f.sale_id} className="pcx-slot pcx-slot--free">
                                        <BuyerLink id={f.buyer_id} name={f.buyer} />
                                        <span className="pcx-slot__meta">
                                            {f.ordered_at && <span className="pcx-slot__ordered">Encargado {formatDateOnly(f.ordered_at)}</span>}
                                            {f.reopened_by && <span className="pcx-slot__reopened">{REOPENED[f.reopened_by] || 'Vacante'}</span>}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
